import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaLink } from 'react-icons/fa';
import { integrationsAPI } from '../services/api';
import { Modal, ModalFooter } from './Modals';

// Link each PrimeClean user to an employee in the LMS / SAP / other app. Suggestions
// (employee ID → e-mail → unique full name) are pre-selected; nothing is saved until
// "Save links". The employee ID is what training completions, pulls and the LMS
// webhook use to find the right person.
const EmployeeMatchModal = ({ source, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [choice, setChoice] = useState({});
  const [filter, setFilter] = useState('');
  const [onlyUnlinked, setOnlyUnlinked] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    integrationsAPI.employeeMatches(source.key).then((res) => {
      const d = res?.data?.data || { users: [], employees: [] };
      setData(d);
      setChoice(Object.fromEntries(d.users.map((u) => [u.user_id, u.linked ? u.employee_id : (u.suggestion || '')])));
    }).catch((error) => {
      toast.error(error?.response?.data?.message || `Could not load employees from ${source.label}`);
      onClose();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.key]);

  const usedBy = useMemo(() => {
    const m = {};
    Object.entries(choice).forEach(([uid, eid]) => { if (eid) (m[String(eid).toLowerCase()] = m[String(eid).toLowerCase()] || []).push(uid); });
    return m;
  }, [choice]);

  if (!data) return <Modal isOpen onClose={onClose} title={`Match employees — ${source.label}`} size="lg"><div className="loading-container"><div className="spinner" /></div></Modal>;

  const q = filter.trim().toLowerCase();
  const rows = data.users.filter((u) => (!onlyUnlinked || !u.linked) &&
    (!q || [u.username, u.employee_name, u.email, u.employee_id].some((v) => String(v || '').toLowerCase().includes(q))));
  const changes = data.users.filter((u) => String(choice[u.user_id] || '') !== String(u.employee_id || ''));
  const duplicates = Object.entries(usedBy).filter(([, uids]) => uids.length > 1).map(([eid]) => eid);

  const save = async () => {
    if (duplicates.length) return toast.warning(`The same employee is chosen for two users: ${duplicates.join(', ')}`);
    setSaving(true);
    try {
      const res = await integrationsAPI.linkEmployees(source.key, { links: changes.map((u) => ({ user_id: u.user_id, employee_id: choice[u.user_id] || null })) });
      toast.success(res?.data?.message || 'Links saved');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not save links');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={<><FaLink /> Match employees — {source.label}</>} size="xl">
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: 0 }}>
        {data.users.filter((u) => u.linked).length} of {data.users.length} users are linked to {data.employees.length} {source.label} employees.
        Suggestions come from the same employee ID, the same e-mail, or a unique matching name — check them before saving.
      </p>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
        <input className="form-control" style={{ maxWidth: '280px' }} placeholder="Search user…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} /> Only users not linked yet</label>
      </div>
      <div className="table-container" style={{ maxHeight: '55vh', overflow: 'auto' }}>
        <table>
          <thead><tr><th>User</th><th>E-mail</th><th>Current employee ID</th><th>{source.label} employee</th><th>Suggested by</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} className="text-center text-muted">Nothing to show.</td></tr> : rows.map((u) => {
              const sel = choice[u.user_id] || '';
              const dup = sel && duplicates.includes(String(sel).toLowerCase());
              return (
                <tr key={u.user_id}>
                  <td><strong>{u.employee_name || u.username}</strong><div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{u.username} · {u.role}</div></td>
                  <td style={{ fontSize: 'var(--fs-sm)' }}>{u.email || '—'}</td>
                  <td>{u.employee_id || '—'}{u.stale_link && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger-color, #dc2626)' }}>not found in {source.label}</div>}</td>
                  <td>
                    <select className="form-control" style={{ minWidth: '260px', borderColor: dup ? 'var(--danger-color, #dc2626)' : undefined }} value={sel}
                      onChange={(e) => setChoice((p) => ({ ...p, [u.user_id]: e.target.value }))}>
                      <option value="">— not linked —</option>
                      {data.employees.map((e) => (
                        <option key={e.employeeId} value={e.employeeId}>{e.employeeId} — {e.name}{e.department ? ` (${e.department})` : ''}{e.linked && e.employeeId !== u.employee_id ? ' • linked' : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ fontSize: 'var(--fs-sm)' }}>{u.suggestion_reason || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ModalFooter>
        <span style={{ marginRight: 'auto', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{changes.length} change(s)</span>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !changes.length}>{saving ? 'Saving…' : 'Save links'}</button>
      </ModalFooter>
    </Modal>
  );
};

export default EmployeeMatchModal;
